import { cn } from '@ifixit/ui/lib/utils';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from './icon';
import type { ComponentProps } from 'react';

function Pagination({ className, ...props }: ComponentProps<'nav'>) {
   return (
      <nav
         role="navigation"
         aria-label="pagination"
         data-slot="pagination"
         className={cn('mx-auto flex w-full justify-center', className)}
         {...props}
      />
   );
}

function PaginationContent({ className, ...props }: ComponentProps<'ul'>) {
   return (
      <ul
         data-slot="pagination-content"
         className={cn('flex flex-row items-center gap-1', className)}
         {...props}
      />
   );
}

function PaginationItem({ ...props }: ComponentProps<'li'>) {
   return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
   isActive?: boolean;
} & ComponentProps<'a'>;

function PaginationLink({ className, isActive, ...props }: PaginationLinkProps) {
   return (
      <Button asChild>
         <a
            aria-current={isActive ? 'page' : undefined}
            data-active={isActive}
            className={cn(
               isActive ? 'button-primary' : 'button-ghost',
               'button-icon',
               isActive && 'bg-brand-100 text-black hover:bg-brand-100',
               className
            )}
            {...props}
         />
      </Button>
   );
}

function PaginationPrevious({ className, ...props }: ComponentProps<typeof PaginationLink>) {
   return (
      <PaginationLink
         aria-label="Go to previous page"
         className={cn('button-secondary button-md', className)}
         {...props}
      >
         <FaIcon name="chevron-left" />
         <span className="sr-only">Previous</span>
      </PaginationLink>
   );
}

function PaginationNext({ className, ...props }: ComponentProps<typeof PaginationLink>) {
   return (
      <PaginationLink
         aria-label="Go to next page"
         className={cn('button-secondary button-md', className)}
         {...props}
      >
         <span className="sr-only">Next</span>
         <FaIcon name="chevron-right" />
      </PaginationLink>
   );
}

function PaginationFirst({ className, ...props }: ComponentProps<typeof PaginationLink>) {
   return (
      <PaginationLink
         aria-label="Go to first page"
         className={cn('button-secondary button-md', className)}
         {...props}
      >
         <FaIcon name="chevrons-left" />
         <span className="sr-only">First</span>
      </PaginationLink>
   );
}

function PaginationLast({ className, ...props }: ComponentProps<typeof PaginationLink>) {
   return (
      <PaginationLink
         aria-label="Go to last page"
         className={cn('button-secondary button-md', className)}
         {...props}
      >
         <FaIcon name="chevrons-right" />
         <span className="sr-only">Last</span>
      </PaginationLink>
   );
}

function PaginationEllipsis({ className, ...props }: ComponentProps<'span'>) {
   return (
      <span
         aria-hidden
         data-slot="pagination-ellipsis"
         className={cn('flex size-9 items-center justify-center', className)}
         {...props}
      >
         <FaIcon name="ellipsis" className="size-4" />
         <span className="sr-only">More pages</span>
      </span>
   );
}

export interface PageRange {
   start: number;
   end: number;
}

export function calculatePageRange(
   currentPage: number,
   totalPages: number,
   visiblePages: number
): PageRange {
   if (totalPages <= visiblePages) {
      return { start: 1, end: totalPages };
   }

   const halfVisible = Math.floor(visiblePages / 2);

   if (currentPage <= halfVisible + 1) {
      return { start: 1, end: visiblePages };
   }

   if (currentPage >= totalPages - halfVisible) {
      return {
         start: Math.max(1, totalPages - visiblePages + 1),
         end: totalPages,
      };
   }

   return {
      start: currentPage - halfVisible,
      end: Math.min(totalPages, currentPage + halfVisible),
   };
}

export {
   Pagination,
   PaginationContent,
   PaginationLink,
   PaginationItem,
   PaginationPrevious,
   PaginationNext,
   PaginationFirst,
   PaginationLast,
   PaginationEllipsis,
};
